/**
 * CommentPin Component
 * Task 5.4: Comment Pins
 *
 * Displays a numbered pin on a commented element.
 * Uses PDS design tokens for consistent Pantheon branding.
 *
 * Features:
 * - Numbered pins positioned at element locations
 * - Status indication (open/resolved via data attribute)
 * - Unread state indicators (red dot + dashed border)
 * - Click to expand thread view
 * - Hover to highlight element boundary
 * - Reply count badge
 * - Full keyboard accessibility
 *
 * Architecture:
 * - Pure presentational component (no useEffect)
 * - Position controlled by parent (PinManager)
 * - Uses data attributes for styling hooks (not inline styles for state)
 */

import React, { useRef, useCallback } from 'react';
import type { Comment } from '../../lib/types';
import { tokens } from '../../styles/tokens';

export interface CommentPinProps {
  comment: Comment;
  pinNumber: number;
  position?: { top: number; left: number };
  onClick: (comment: Comment, pinElement: HTMLElement) => void;
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
  isExpanded?: boolean;
  isUnread?: boolean;
  replyCount?: number;
  /** Author info for displaying initials instead of numbers */
  author?: {
    name: string;
    initials: string;
    avatar?: string | null;
  };
}

export const CommentPin: React.FC<CommentPinProps> = ({
  comment,
  pinNumber,
  position,
  onClick,
  onHoverStart,
  onHoverEnd,
  isExpanded = false,
  isUnread = false,
  replyCount,
  author,
}) => {
  const pinRef = useRef<HTMLDivElement>(null);

  // Display author initials if available, otherwise fall back to pin number
  const displayContent = author?.initials || pinNumber.toString();

  const handleClick = useCallback(() => {
    if (pinRef.current) {
      onClick(comment, pinRef.current);
    }
  }, [comment, onClick]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (pinRef.current) {
        onClick(comment, pinRef.current);
      }
    }
  };

  const handleMouseEnter = () => {
    onHoverStart?.();
  };

  const handleMouseLeave = () => {
    onHoverEnd?.();
  };

  // Generate accessible label
  const getAriaLabel = () => {
    let label = `Comment ${pinNumber}`;
    if (comment.status === 'resolved') {
      label += ' (resolved)';
    }
    if (isUnread) {
      label += ' (unread)';
    }
    if (replyCount && replyCount > 0) {
      label += `, ${replyCount} ${replyCount === 1 ? 'reply' : 'replies'}`;
    }
    return label;
  };

  return (
    <div
      ref={pinRef}
      data-testid={`comment-pin-${pinNumber}`}
      data-multidev-sdk="true"
      data-comment-id={comment.id}
      data-status={comment.status}
      data-expanded={isExpanded ? 'true' : 'false'}
      role="button"
      tabIndex={0}
      aria-label={getAriaLabel()}
      aria-expanded={isExpanded}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        position: position ? 'absolute' : 'relative',
        ...(position && { top: `${position.top}px`, left: `${position.left}px` }),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '32px',
        height: '32px',
        borderRadius: '50%',
        backgroundColor: comment.status === 'open'
          ? tokens.colors.primary
          : tokens.colors.gray400,
        color: tokens.colors.textReverse,
        fontSize: '12px',
        fontWeight: tokens.typography.fontWeight.bold,
        cursor: 'pointer',
        zIndex: 999997,
        boxShadow: isExpanded
          ? `0 0 0 3px ${tokens.colors.primaryLight}, ${tokens.elevation.raised}`
          : tokens.elevation.raised,
        transition: `transform ${tokens.animation.duration} ${tokens.animation.timing}, box-shadow ${tokens.animation.duration} ${tokens.animation.timing}, border-color ${tokens.animation.duration} ${tokens.animation.timing}`,
        transform: isExpanded ? 'scale(1.1)' : 'scale(1)',
        outline: 'none',
        border: isExpanded
          ? `2px solid ${tokens.colors.primary}`
          : isUnread
            ? `2px dashed ${tokens.colors.white}`
            : `2px solid ${tokens.colors.white}`,
      }}
    >
      {/* Author avatar or initials */}
      {author?.avatar ? (
        <img
          src={author.avatar}
          alt=""
          style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            objectFit: 'cover',
          }}
        />
      ) : (
        displayContent
      )}

      {/* Reply count badge - shown when there are replies */}
      {replyCount !== undefined && replyCount > 0 && (
        <span
          style={{
            position: 'absolute',
            bottom: `calc(-1 * ${tokens.spacing['5xs']})`,
            right: `calc(-1 * ${tokens.spacing['5xs']})`,
            width: '16px',
            height: '16px',
            borderRadius: '8px',
            backgroundColor: isExpanded ? tokens.colors.primary : tokens.colors.gray500,
            color: tokens.colors.textReverse,
            fontSize: '10px',
            fontWeight: tokens.typography.fontWeight.bold,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${tokens.colors.white}`,
          }}
        >
          {replyCount}
        </span>
      )}

      {/* Unread indicator - red dot in top-right corner */}
      {isUnread && (
        <span
          data-testid="unread-indicator"
          aria-label="Unread comment"
          style={{
            position: 'absolute',
            top: `calc(-1 * ${tokens.spacing['5xs']})`,
            right: `calc(-1 * ${tokens.spacing['5xs']})`,
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: tokens.colors.critical,
            border: `1px solid ${tokens.colors.white}`,
          }}
        />
      )}

    </div>
  );
};
